import React, { useCallback, useEffect, useState } from 'react';
import { Box, Dialog, DialogContent, DialogTitle, FormControl, FormControlLabel, RadioGroup, TextField, Typography, useTheme } from "@mui/material";
import CheckoutHeader from '../components/CheckoutHeader';
import { useAppDispatch, useAppSelector } from '../hooks';
import MenuItem from '../components/MenuItem';
import { t } from '@lingui/macro';
import orderSlice from '../store/orderSlice';
import { TMenuItem, PaymentMethod, ShippingMethod, TWorkingHours, TServerError } from '../types';
import StyledButton from '../components/StyledButton';
import StyledPaper from '../components/StyledPaper';
import FmdGoodOutlinedIcon from '@mui/icons-material/FmdGoodOutlined';
import QrCodeScannerOutlinedIcon from '@mui/icons-material/QrCodeScannerOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import api from '../api';
import { DesktopTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import userSlice from '../store/userSlice';
import { Link, useNavigate } from 'react-router-dom';
import appRoutes from '../utils/routes';
import EditablePaper from '../components/EditablePaper';
import dayjs, { Dayjs } from 'dayjs';
import StyledRadio from '../components/StyledRadio';
import { HTTPError } from 'ky';
import { isValidPhoneNumber } from 'libphonenumber-js';
import PriceRender from '../components/PriceRender';
import currentOrderSlice from '../store/orderSlice';

const PaymentMethodSelector: React.FC<{
    availableMethods: PaymentMethod[], currentMethod?: PaymentMethod, onChange: (value: PaymentMethod) => void
}> = (props) => {
    const { availableMethods, currentMethod, onChange } = props
    const labels = {
        [PaymentMethod.online]: t`Online`,
        [PaymentMethod.cashInStore]: t`Cash in store`,
        [PaymentMethod.cardInStore]: t`Card in store`
    }

    return <FormControl>
        <RadioGroup
            value={currentMethod}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange((event.target as HTMLInputElement).value as PaymentMethod);
            }}
        >
            {availableMethods.map(method => <FormControlLabel
                key={method}
                value={method}
                control={<StyledRadio />}
                label={labels[method]}
            />)}
        </RadioGroup>
    </FormControl>

}

const ShippingMethodSelector: React.FC<{
    availableMethods: ShippingMethod[], currentMethod?: ShippingMethod, onChange: (value: ShippingMethod) => void
}> = (props) => {
    const { availableMethods, currentMethod, onChange } = props
    const labels = {
        [ShippingMethod.delivery]: t`Delivery`,
        [ShippingMethod.takeout]: t`Takeaway`,
        [ShippingMethod.dineIn]: t`Dine-in`,
    }

    return <FormControl>
        <RadioGroup
            value={currentMethod}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChange((event.target as HTMLInputElement).value as ShippingMethod);
            }}
        >
            {availableMethods.map(method => <FormControlLabel
                key={method}
                value={method}
                control={<StyledRadio />}
                label={labels[method]}
            />)}
        </RadioGroup>
    </FormControl>
}

const CheckoutScreen: React.FC = () => {
    const timePickerStep = 5;

    const dispatch = useAppDispatch();
    const theme = useTheme();
    const navigate = useNavigate();

    const currentOrder = useAppSelector(state => state.order)
    const currentOrderItems = useAppSelector(state => state.order.items);
    const currentRestaurant = useAppSelector(state => state.restaurant.info);
    const deliveryType = useAppSelector(state => state.order.shippingMethod);
    const paymentMethod = useAppSelector(state => state.order.paymentMethod);
    const deliveryDate = useAppSelector(state => state.order.deadlineDate);
    const comment = useAppSelector(state => state.order.comment);
    const userName = useAppSelector(state => state.user.name);
    const userPhoneNumber = useAppSelector(state => state.user.phoneNumber);
    const isUserVerified = useAppSelector(state => state.user.isVerified);
    const currentUserId = useAppSelector(state => state.user._id)
    const hasCurrentOrder = useAppSelector(state => state.user.hasCurrentOrder)

    const isOrderReady = currentOrderItems.length > 0 && isUserVerified && (deliveryType !== undefined);

    const [workingHoursForToday, setWorkingHoursForToday] = useState<TWorkingHours | undefined>()
    const [minumumOrderTime, setMinumumOrderTime] = useState<dayjs.Dayjs | undefined>()
    const [maximumOrderTime, setMaximumOrderTime] = useState<dayjs.Dayjs | undefined>()


    const [serverError, setServerError] = useState<TServerError | undefined>()

    const setDeliveryType = useCallback(
        (method: ShippingMethod) => { dispatch(orderSlice.actions.changeShippingMethod(method)) },
        [dispatch]
    );

    const setPaymentMethod = useCallback(
        (method: PaymentMethod) => { dispatch(orderSlice.actions.changePaymentMethod(method)) },
        [dispatch]
    );

    const setDeliveryDate = useCallback(
        (date: Dayjs) => { dispatch(orderSlice.actions.changeDeadlineDate(date.toDate())) },
        [dispatch]
    );

    const setUserName = useCallback(
        (username: string) => {
            dispatch(userSlice.actions.changeUsername(username))
        },
        [dispatch]
    );

    const setUserPhoneNumber = useCallback(
        (phoneNumber: string) => {
            dispatch(userSlice.actions.changePhoneNumber(phoneNumber))
        },
        [dispatch]
    );

    const setOrderComment = useCallback(
        (comment: string) => {
            dispatch(orderSlice.actions.changeComment(comment))
        },
        [dispatch]
    );

    const onAddClick = useCallback(
        (item: TMenuItem) => dispatch(orderSlice.actions.addItemToOrder(item)),
        [dispatch]
    );
    const onRemoveClick = useCallback(
        (item: TMenuItem) => dispatch(orderSlice.actions.removeItemFromOrder(item)),
        [dispatch]
    );

    const onEditUserDataClick = useCallback(
        () => dispatch(userSlice.actions.changeUserVerification(false)),
        [dispatch]
    )

    const onEditDeliveryInfoClick = useCallback(
        () => { dispatch(orderSlice.actions.changeDeliveryLocation(undefined)) },
        [dispatch]
    )

    const onEditDineInClick = useCallback(
        () => {
            dispatch(orderSlice.actions.changeDineInPlace(undefined))
            navigate(appRoutes.table)
        },
        [dispatch, navigate]
    )

    const onPlaceOrderClick = useCallback(
        async () => {
            try {
                const updatedOrder = await api.placeOrder(currentOrder, currentUserId!)
                dispatch(orderSlice.actions.setOrderFromServer(updatedOrder))
                dispatch(userSlice.actions.setHasCurrentOrder(true))
                navigate(appRoutes.orderStatus)
            } catch (err) {
                if ((err as HTTPError).name === 'HTTPError') {
                    const errResponse = await (err as HTTPError).response.json()
                    console.log(errResponse)
                    setServerError(errResponse as TServerError)
                }
            }
        },
        [currentOrder, currentUserId, dispatch, navigate]
    )

    useEffect(() => { (currentOrderItems.length === 0 || hasCurrentOrder) && navigate(appRoutes.menu) })
    useEffect(() => { api.getWorkingHoursForToday().then(data => setWorkingHoursForToday(data)) }, [])
    useEffect(() => { window.scrollTo({ top: 0 }) }, [])

    useEffect(() => {
        if (!workingHoursForToday) return


        for (let i = 0; i < workingHoursForToday!.hours!.length; i++) {

            const opening = workingHoursForToday!.hours![i].opening
            const closing = workingHoursForToday!.hours![i].closing

            // Hard coded 5 minutes.
            const value = dayjs(closing).subtract(5, "minute")
            setMaximumOrderTime(value)

            if (dayjs() < dayjs(opening)) {
                const value = dayjs(workingHoursForToday!.hours![i].opening).add(currentRestaurant!.minMinutesBeforeTakeout, "minute")
                setMinumumOrderTime(value)

                if (value > dayjs(deliveryDate)) {
                    setDeliveryDate(value)
                }
            } else {
                const currentMinute = dayjs().add(currentRestaurant!.minMinutesBeforeTakeout, 'minute').minute()

                const minutesDiff = currentMinute % timePickerStep
                const addMintues = timePickerStep - minutesDiff
                const value = dayjs().add(currentRestaurant!.minMinutesBeforeTakeout + addMintues, 'minute')
                setMinumumOrderTime(value)

                if (value > dayjs(deliveryDate)) {
                    setDeliveryDate(value)
                }

            }
        }

    }, [workingHoursForToday])

    if (!currentRestaurant) return null

    return <Box>
        <Box>
            <CheckoutHeader restaurantName={currentRestaurant!.name} />
        </Box>
        <section id='order-items'>
            {currentOrderItems.map((item) =>
                <Box key={item._id} pt='12px'>
                    <MenuItem
                        item={item}
                        quantityInOrder={item.orderCount}
                        withOptions
                        isCheckout
                        onShowClick={() => {}}
                        onAddClick={() => onAddClick(item)}
                        onRemoveClick={() => onRemoveClick(item)}
                    />
                </Box>)
            }</section>
        <Box pb='20px' />
        <section id='order-comment'>
            <StyledPaper>
                <Typography>{t`Comment`}</Typography>
                <Box pb='6px' />
                <TextField
                    fullWidth
                    multiline
                    rows={3}
                    color='secondary'
                    placeholder={t`Ex. don't add onion`}
                    value={comment}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        setOrderComment((event.target as HTMLInputElement).value);
                    }}
                />
            </StyledPaper>
        </section>
        <Box pb='20px' />
        <section id='delivery-type'>
            <StyledPaper>
                <ShippingMethodSelector
                    availableMethods={currentRestaurant!.shippingMethods}
                    currentMethod={deliveryType}
                    onChange={setDeliveryType}
                />
            </StyledPaper>
        </section>
        <Box pb='20px' />
        <section id='delivery-details'>
            {deliveryType === ShippingMethod.dineIn &&
                <>{currentOrder.dineInPlace ?
                    <EditablePaper onEditClick={onEditDineInClick}>
                        <Typography>{t`Table #`}{currentOrder.dineInPlace}</Typography>
                    </EditablePaper>
                    : <StyledPaper sx={{ display: 'flex', justifyContent: 'center' }}>
                        <StyledButton variant='text' color='secondary' fullWidth onClick={() => { navigate(appRoutes.table) }}>
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                <Typography variant='subtitle1'>{t`Scan QR code at your table`}</Typography>
                                <QrCodeScannerOutlinedIcon sx={{ px: '8px' }} />
                            </Box>
                        </StyledButton>
                    </StyledPaper>
                }</>
            }
            {deliveryType === ShippingMethod.takeout &&
                <StyledPaper sx={{ display: 'flex', justifyContent: 'center' }}>
                    <Box display={'flex'} flexDirection={'column'} flexGrow={1}>
                        <Typography variant='subtitle1'>{t`Takeaway location`}</Typography>
                        <Typography variant='subtitle2'>{currentRestaurant?.name}</Typography>
                        <Typography variant='subtitle2'>{currentRestaurant?.address}</Typography>
                    </Box>
                </StyledPaper>
            }
            {deliveryType === ShippingMethod.delivery &&
                <>{currentOrder.deliveryLocation
                    ? <EditablePaper title={t`Delivery location`} onEditClick={onEditDeliveryInfoClick}>
                        <Typography variant='subtitle1'>{currentOrder.deliveryLocation.address}</Typography>
                        <Typography variant='subtitle2'>{currentOrder.deliveryLocation.comment}</Typography>
                    </EditablePaper>
                    : <StyledPaper sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Link to={appRoutes.address} style={{ width: '100%' }}>
                            <StyledButton
                                variant='text'
                                color='secondary'
                                fullWidth
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant='subtitle1'>
                                        {t`Select delivery address`}
                                    </Typography>
                                    <FmdGoodOutlinedIcon sx={{ px: '8px' }} />
                                </Box>
                            </StyledButton>
                        </Link>
                    </StyledPaper>
                }
                </>
            }
            <Box pb='20px' />
        </section>
        <section id='time-picker'>
            {deliveryType !== ShippingMethod.dineIn
                && <>
                    <StyledPaper>
                        <Typography>{
                            deliveryType === 'delivery' ? t`Delivery time` : t`Takeaway time`}
                        </Typography>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DesktopTimePicker
                                sx={{ width: '100%', color: theme.palette.secondary.light }}
                                minutesStep={timePickerStep}
                                ampm={false}
                                ampmInClock={false}
                                defaultValue={minumumOrderTime}
                                minTime={minumumOrderTime}
                                maxTime={maximumOrderTime}
                                disableIgnoringDatePartForTimeValidation
                                disablePast
                                value={dayjs(deliveryDate)}
                                onChange={(value: Dayjs | null) => {
                                    console.log("change")
                                    Boolean(value) && value !== null && setDeliveryDate(value);
                                }}
                            />
                        </LocalizationProvider>
                    </StyledPaper>
                    <Box pb='20px' />
                </>
            }
        </section>
        <section id='user-details'>
            {isUserVerified
                ? <EditablePaper onEditClick={onEditUserDataClick}>
                    <Typography>{t`Name`}</Typography>
                    <Typography>{userName}</Typography>
                    <Typography>{t`Phone number`}</Typography>
                    <Typography>{userPhoneNumber}</Typography>
                </EditablePaper>
                : <StyledPaper sx={{ display: 'flex', flexDirection: 'column' }}>
                    <TextField
                        label={t`Name`}
                        sx={{ borderRadius: '16px' }}
                        fullWidth
                        color='secondary'
                        placeholder={t`Name`}
                        value={userName}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                            setUserName((event.target as HTMLInputElement).value);
                        }}
                    />
                    <Box pb='12px' />
                    <Autocomplete
                        sx={{ borderRadius: '16px' }}
                        fullWidth
                        color='secondary'
                        freeSolo
                        disablePortal
                        options={["🇧🇪 BE +32", "🇳🇱 NL +31", "🇩🇪 DE +49", "🇫🇷 FR +33", "🇷🇴 RO: +40", "🇺🇦 УКР +38"]}
                        renderInput={(params) =>
                            <TextField
                                type='phone' {...params}
                                label={t`Phone Number`}
                                placeholder={t`Phone Number`}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setUserPhoneNumber((event.target as HTMLInputElement).value);
                                }} />}
                    />
                    {userName && userName.length > 1 && userPhoneNumber && <>
                        <Box pb='12px' />
                        <Link to={appRoutes.phone} style={{ height: '100%' }}>
                            <StyledButton variant='text' color='secondary' fullWidth>
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                                    <Typography variant='subtitle1'>
                                        {t`Confirm phone number`}
                                    </Typography>
                                    <SendOutlinedIcon sx={{ px: '8px' }} />
                                </Box>
                            </StyledButton>
                        </Link>
                    </>}
                </StyledPaper>}
        </section>
        <Box pb='20px' />
        <section id='payment-method'>
            <StyledPaper>
                <PaymentMethodSelector
                    availableMethods={currentRestaurant!.paymentMethods}
                    currentMethod={paymentMethod}
                    onChange={setPaymentMethod}
                />
            </StyledPaper>
        </section>
        <Box pb='20px' />
        {isOrderReady && <><StyledButton sx={{ position: 'sticky', bottom: '15px' }} variant='contained' fullWidth onClick={onPlaceOrderClick}>
            <Box mr={'8px'}>{t`Place order`}</Box>
            <PriceRender price={currentOrder.total} />
        </StyledButton>
            <Box pb='12px' />
        </>}
        <Dialog
            open={Boolean(serverError)}
            onClose={() => { setServerError(undefined) }}
        >
            <Box p={'24px'}>
                <Box display={'flex'} flexDirection={'column'} flexGrow={1} >
                    <DialogTitle sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography fontSize={16} fontWeight={600}>
                            {serverError?.title || t`Oops!`}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Typography fontSize={14} fontWeight={500}>
                            {serverError?.message}
                        </Typography>
                    </DialogContent>
                </Box>
                <StyledButton
                    variant={'contained'}
                    fullWidth
                    onClick={() => { setServerError(undefined) }}
                >
                    {t`Ok`}
                </StyledButton>
            </Box>
        </Dialog>
    </Box>
}

export default CheckoutScreen;